import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageSeo } from "@/components/PageSeo";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Gift, Zap, Crown, HelpCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const EstylingPricing = () => {
  const { t } = useTranslation();
  const { buildPath } = useLocalizedPath();

  const packages = [
    { key: 'starter', icon: Gift, credits: 25, price: '249', popular: false },
    { key: 'standard', icon: Zap, credits: 50, price: '449', popular: true },
    { key: 'pro', icon: Crown, credits: 100, price: '799', popular: false },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageSeo />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-primary/5 to-background py-16 sm:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground">
                {t('seoPages.pricing.hero.title')}
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                {t('seoPages.pricing.hero.subtitle')}
              </p>
              <Button asChild size="lg" className="text-lg px-8">
                <Link to={buildPath('/auth')}>{t('seoPages.pricing.cta')}</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Free credits */}
        <section className="py-12 bg-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Gift className="h-12 w-12 mx-auto mb-4 text-primary" />
              <h2 className="text-2xl font-bold mb-2 text-foreground">
                {t('seoPages.pricing.freeCredits.title')}
              </h2>
              <p className="text-muted-foreground">
                {t('seoPages.pricing.freeCredits.text')}
              </p>
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-16 sm:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center text-foreground">
                {t('seoPages.pricing.packages.title')}
              </h2>
              <p className="text-lg text-muted-foreground text-center mb-12">
                {t('seoPages.pricing.packages.subtitle')}
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {packages.map(({ key, icon: Icon, credits, price, popular }) => (
                  <Card key={key} className={`relative ${popular ? 'border-2 border-primary shadow-lg' : ''}`}>
                    {popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-primary text-primary-foreground text-sm px-4 py-1 rounded-full">
                          {t('seoPages.pricing.packages.popular')}
                        </span>
                      </div>
                    )}
                    <CardHeader className="text-center pt-8">
                      <Icon className={`h-10 w-10 mx-auto mb-4 ${popular ? 'text-primary' : 'text-muted-foreground'}`} />
                      <CardTitle className="text-xl">
                        {t(`seoPages.pricing.packages.items.${key}.name`)}
                      </CardTitle>
                      <div className="mt-4">
                        <span className="text-4xl font-bold text-foreground">{price}</span>
                        <span className="text-muted-foreground"> {t('seoPages.pricing.packages.currency')}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">
                        {credits} {t('seoPages.pricing.packages.credits')}
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-3 pb-8">
                      <p className="text-center text-muted-foreground mb-4">
                        {t(`seoPages.pricing.packages.items.${key}.perImage`)}
                      </p>
                      {['noExpiry', 'allFeatures', 'support'].map((feature) => (
                        <div key={feature} className="flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">
                            {t(`seoPages.pricing.packages.features.${feature}`)}
                          </span>
                        </div>
                      ))}
                      <Button asChild className="w-full mt-6" variant={popular ? 'default' : 'outline'}>
                        <Link to={buildPath('/auth')}>{t('seoPages.pricing.cta')}</Link>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* What is included */}
        <section className="py-16 sm:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-foreground">
                {t('seoPages.pricing.included.title')}
              </h2>
              <div className="grid sm:grid-cols-2 gap-6">
                {['empty', 'style', 'replace', 'download', 'unlimited', 'support'].map((item) => (
                  <div key={item} className="flex items-start gap-4">
                    <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold mb-1 text-foreground">
                        {t(`seoPages.pricing.included.items.${item}.title`)}
                      </h3>
                      <p className="text-muted-foreground">
                        {t(`seoPages.pricing.included.items.${item}.text`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-center gap-3 mb-12">
                <HelpCircle className="h-8 w-8 text-primary" />
                <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                  {t('seoPages.pricing.faq.title')}
                </h2>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {['howCredits', 'expire', 'refund', 'business', 'payment'].map((item) => (
                  <AccordionItem key={item} value={item}>
                    <AccordionTrigger className="text-left">
                      {t(`seoPages.pricing.faq.items.${item}.q`)}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {t(`seoPages.pricing.faq.items.${item}.a`)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-24 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('seoPages.pricing.finalCta.title')}
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              {t('seoPages.pricing.finalCta.text')}
            </p>
            <Button asChild size="lg" variant="secondary" className="text-lg px-8">
              <Link to={buildPath('/auth')}>{t('seoPages.pricing.cta')}</Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default EstylingPricing;
